/**
 * Mission-level rules: the clock, Recall and the Osprey (02 §10), alert decay and
 * reinforcements (02 §6.3, §9.3), capture, and how a mission ends.
 */

import { raiseAlert } from './alert';
import { chooseLandingZone, contestPickup, hoverTicks } from './conditions';
import { type Ctx, isFighter } from './ctx';
import { spawnReinforcements } from './enemies';
import { escortsAtLadder, recallAllowed } from './objectives';
import { scoreMission } from './scoring';
import { ALERT, OSPREY, RANGER } from './tuning';
import type { Outcome } from './types';

/** Runs before the ranger's own step so it sees this tick's button press. */
export function stepRecall(ctx: Ctx): void {
  const { world: w, cmd } = ctx;
  const r = w.ranger;
  if (cmd.recall && !r.prevRecall && r.status === 'active' && w.osprey.phase === 'away' && recallAllowed(ctx.statics.def)) {
    w.osprey.phase = 'inbound';
    w.osprey.ticks = OSPREY.inboundTicks;
    w.osprey.recalled = true;
    ctx.events.push({ type: 'osprey', phase: 'inbound' });
  }
  r.prevRecall = cmd.recall;
}

function finish(ctx: Ctx, outcome: Outcome): void {
  const w = ctx.world;
  if (w.result) return;
  w.result = scoreMission(w, ctx.statics.def, outcome);
  ctx.events.push({ type: 'ended', outcome });
}

export function stepMission(ctx: Ctx): void {
  const w = ctx.world;
  const r = w.ranger;
  const def = ctx.statics.def;

  if (w.insertion.phase !== 'ground') {
    if (w.insertion.scrubbed) finish(ctx, 'scrubbed');
    return;
  }

  /* ---- clock: at zero the Osprey comes whether called or not */
  if (w.clockTicks > 0) w.clockTicks--;
  if (w.clockTicks === 0 && w.osprey.phase === 'away') {
    w.osprey.phase = 'inbound';
    w.osprey.ticks = 0;
    ctx.events.push({ type: 'osprey', phase: 'inbound' });
  }

  /* ---- osprey */
  const o = w.osprey;
  // Whoever sent for it - Recall, the clock, the mission itself - the garrison saw it turn in.
  if (ctx.events.some((ev) => ev.type === 'osprey' && ev.phase === 'inbound')) {
    // Alternate LZ (04 §8) first: the squad Hot Extraction sends must go to the X it is actually coming to.
    chooseLandingZone(ctx);
    contestPickup(ctx);
  }
  if (o.phase === 'inbound') {
    o.ticks--;
    if (o.ticks <= 0) {
      o.phase = 'hovering';
      o.ticks = hoverTicks(def);
      ctx.events.push({ type: 'osprey', phase: 'hovering' });
    }
  } else if (o.phase === 'hovering') {
    const dx = r.x - w.objective.pickupX;
    const dz = r.z - w.objective.pickupZ;
    const atLadder = dx * dx + dz * dz <= RANGER.boardRadius * RANGER.boardRadius;
    // OBJ: escorts climb first, a second each (02 §10). The ranger waits his turn while they are on or
    // making for the ladder, and the Osprey does not pull away from under a man who is climbing.
    const escorts = escortsAtLadder(w);
    if (r.status === 'active' && atLadder && r.action === 'none' && !escorts.blocking) {
      r.status = 'boarding';
      r.action = 'board';
      r.actionTicks = 0;
      r.dragging = -1;
    }
    if (r.status === 'boarding') {
      if (!atLadder) {
        r.status = 'active';
        r.action = 'none';
      } else {
        r.actionTicks++;
        if (r.actionTicks >= RANGER.boardTicks) {
          // Boarding ends the mission whether or not the goal was met: Recall is the abort.
          r.status = 'extracted';
          o.phase = 'departed';
          ctx.events.push({ type: 'osprey', phase: 'departed' });
        }
      }
    } else if (!escorts.climbing) {
      o.ticks--;
      if (o.ticks <= 0) {
        o.phase = 'departed';
        w.stranded = true;
        ctx.events.push({ type: 'osprey', phase: 'departed' });
        raiseAlert(ctx, 3, r.x, r.z);
      }
    }
  }

  /* ---- alert: decays a level per quiet spell, never below 1 once raised, never from 3 */
  if (w.combatTicks > 0) w.combatTicks--;
  w.alertQuietTicks++;
  if (w.alert === 2 && w.alertQuietTicks >= ALERT.decayTicks) {
    w.alert = 1;
    w.alertQuietTicks = 0;
    ctx.events.push({ type: 'alert', level: 1 });
  }
  if (w.alert >= 3 && w.reinforcementWaves < ALERT.maxWaves) {
    w.reinforcementTicks--;
    if (w.reinforcementTicks <= 0) {
      w.reinforcementWaves++;
      w.reinforcementTicks = ALERT.waveEveryTicks;
      spawnReinforcements(ctx, ALERT.waveSize);
    }
  }

  /* ---- a downed ranger with the enemy standing over him is taken prisoner */
  if (r.status === 'downed') {
    let near = false;
    for (const e of w.enemies) {
      if (!isFighter(e) || e.state === 'down') continue;
      if ((e.x - r.x) * (e.x - r.x) + (e.z - r.z) * (e.z - r.z) <= RANGER.captureRadius * RANGER.captureRadius) near = true;
    }
    r.captureTicks = near ? r.captureTicks + 1 : 0;
    if (r.captureTicks >= RANGER.captureTicks) r.status = 'captured';
  }

  /* ---- stranded: out of ammunition means capture (the original's rule); the south edge means you walked out */
  if (w.stranded && r.status === 'active') {
    if (r.z >= def.sizeZ - 1.5) r.status = 'walkedOut';
    else if (r.inventory.roundsInMag === 0 && r.inventory.magazines === 0 && r.gear.pistolRounds === 0) {
      for (const e of w.enemies) {
        if (!isFighter(e)) continue;
        if ((e.x - r.x) * (e.x - r.x) + (e.z - r.z) * (e.z - r.z) <= 15 * 15) r.status = 'captured';
      }
    }
  }

  if (w.insertion.scrubbed) finish(ctx, 'scrubbed');
  else if (r.status === 'extracted') finish(ctx, 'extracted');
  else if (r.status === 'kia') finish(ctx, 'kia');
  else if (r.status === 'captured') finish(ctx, 'captured');
  else if (r.status === 'walkedOut') finish(ctx, 'walkedOut');
}
